import { useState, useEffect, useRef, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { API_CONFIG } from '@/constants/api';
import { getAuthToken } from '@/utils/auth';

interface WebRTCOptions {
  userId: string;
  targetUserId?: string;
  onIncomingCall?: (from: string, name: string) => void;
  onCallAccepted?: () => void;
  onCallEnded?: () => void;
}

export function useWebRTC({ userId, targetUserId, onIncomingCall, onCallAccepted, onCallEnded }: WebRTCOptions) {
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  const [isCalling, setIsCalling] = useState(false);
  const [isInCall, setIsInCall] = useState(false);
  const [socket, setSocket] = useState<Socket | null>(null);

  const peerConnection = useRef<RTCPeerConnection | null>(null);
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const remoteVideoRef = useRef<HTMLVideoElement>(null);
  
  const iceCandidatesQueue = useRef<any[]>([]);

  const endCall = useCallback(() => {
    if (peerConnection.current) {
      peerConnection.current.close();
      peerConnection.current = null;
    }
    if (localStream) {
      localStream.getTracks().forEach((track) => track.stop());
      setLocalStream(null);
    }
    setRemoteStream(null);
    setIsInCall(false);
    setIsCalling(false);
    iceCandidatesQueue.current = [];
    if (callbacks.current.onCallEnded) callbacks.current.onCallEnded();
  }, [localStream]);

  // Utilizando um ref para armazenar os callbacks mais recentes e evitar infinite loops 
  // causados por recriação de funções no componente pai
  const callbacks = useRef({ onIncomingCall, onCallAccepted, onCallEnded, endCall });
  
  useEffect(() => {
    callbacks.current = { onIncomingCall, onCallAccepted, onCallEnded, endCall };
  }, [onIncomingCall, onCallAccepted, onCallEnded, endCall]);

  useEffect(() => {
    if (localVideoRef.current && localStream) {
      localVideoRef.current.srcObject = localStream;
    }
  }, [localStream, isInCall]);

  useEffect(() => {
    if (remoteVideoRef.current && remoteStream) {
      remoteVideoRef.current.srcObject = remoteStream;
    }
  }, [remoteStream, isInCall]);

  const servers = {
    iceServers: [
      {
        urls: ['stun:stun1.l.google.com:19302', 'stun:stun2.l.google.com:19302'],
      },
    ],
  };

  useEffect(() => {
    if (!userId) return;
    
    const token = getAuthToken();
    const newSocket = io(`${API_CONFIG.BASE_URL.replace('/api', '')}/consultation`, {
      query: { userId, token },
      transports: ['websocket'],
    });

    newSocket.on('incomingCall', (data: { from: string; name: string; signal: any }) => {
      console.log('Incoming call from:', data.from);
      iceCandidatesQueue.current = []; // Limpa fila de candidatos de chamadas anteriores
      if (callbacks.current.onIncomingCall) callbacks.current.onIncomingCall(data.from, data.name);
      
      // Armazenar o sinal para responder depois
      (window as any).incomingSignal = data.signal;
    });

    newSocket.on('callAccepted', async (signal: any) => {
      console.log('Call accepted');
      setIsInCall(true);
      if (callbacks.current.onCallAccepted) callbacks.current.onCallAccepted();
      if (peerConnection.current) {
        await peerConnection.current.setRemoteDescription(new RTCSessionDescription(signal));
        // Processar candidatos em fila após ter remote description
        while (iceCandidatesQueue.current.length > 0) {
          const candidate = iceCandidatesQueue.current.shift();
          try {
            await peerConnection.current.addIceCandidate(new RTCIceCandidate(candidate));
          } catch (e) {
            console.error('Error adding queued ice candidate', e);
          }
        }
      }
    });

    newSocket.on('iceCandidate', async (candidate: any) => {
      try {
        if (peerConnection.current && peerConnection.current.remoteDescription) {
          await peerConnection.current.addIceCandidate(new RTCIceCandidate(candidate));
        } else {
          iceCandidatesQueue.current.push(candidate);
        }
      } catch (err) {
        console.error('Error adding ice candidate', err);
      }
    });

    newSocket.on('callEnded', () => {
      callbacks.current.endCall();
    });

    setSocket(newSocket);

    return () => {
      newSocket.disconnect();
    };
  }, [userId]);

  const startLocalStream = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      setLocalStream(stream);
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }
      return stream;
    } catch (err) {
      console.error('Error accessing media devices:', err);
      return null;
    }
  }, []);

  const createPeerConnection = useCallback((stream: MediaStream, targetId: string) => {
    const pc = new RTCPeerConnection(servers);
    
    stream.getTracks().forEach((track) => {
      pc.addTrack(track, stream);
    });

    pc.onicecandidate = (event) => {
      if (event.candidate && socket) {
        socket.emit('iceCandidate', { to: targetId, candidate: event.candidate });
      }
    };

    pc.oniceconnectionstatechange = () => {
      console.log('ICE Connection State:', pc.iceConnectionState);
      if (pc.iceConnectionState === 'disconnected' || pc.iceConnectionState === 'failed') {
        console.warn('Conexão perdida, tentando recuperar...');
        // Em um cenário real, poderíamos disparar uma renegociação aqui
      }
    };

    pc.ontrack = (event) => {
      console.log('Received remote track');
      setRemoteStream(event.streams[0]);
      if (remoteVideoRef.current) {
        remoteVideoRef.current.srcObject = event.streams[0];
      }
    };

    peerConnection.current = pc;
    return pc;
  }, [socket]);

  const callUser = async (targetId: string, name: string) => {
    const stream = await startLocalStream();
    if (!stream || !socket) return;

    setIsCalling(true);
    const pc = createPeerConnection(stream, targetId);
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);

    socket.emit('callUser', {
      userToCall: targetId,
      signalData: offer,
      from: userId,
      name: name,
    });
  };

  const answerCall = async (targetId: string) => {
    const stream = await startLocalStream();
    if (!stream || !socket) return;

    const signal = (window as any).incomingSignal;
    const pc = createPeerConnection(stream, targetId);
    
    await pc.setRemoteDescription(new RTCSessionDescription(signal));
    
    // Processar candidatos em fila recebidos antes do setRemoteDescription
    while (iceCandidatesQueue.current.length > 0) {
      const candidate = iceCandidatesQueue.current.shift();
      try {
        await pc.addIceCandidate(new RTCIceCandidate(candidate));
      } catch (e) {
        console.error('Error adding queued ice candidate in answerCall', e);
      }
    }

    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);

    socket.emit('answerCall', { to: targetId, signal: answer });
    setIsInCall(true);
  };

  return {
    localStream,
    remoteStream,
    isCalling,
    isInCall,
    localVideoRef,
    remoteVideoRef,
    callUser,
    answerCall,
    endCall,
    startLocalStream,
  };
}
